import { CSSProperties, MouseEventHandler } from "react";
import Breadcrumb from "react-bootstrap/Breadcrumb";
import { FormattedMessage } from "react-intl";
import Moment from "react-moment";
import { Constants } from "../../../constants";
import {
  EcgDetailsActionType,
  useEcgDetailsContext,
} from "../../../context/EcgDetailsContext";
import useAlert from "../../../hooks/useAlert";
import useCancelRequests from "../../../hooks/useCancelRequests";
import useAuthorizedAction, {
  AuthorizedActionsKeys,
} from "../../../hooks/useAuthorizedAction";
import useResponsive from "../../../hooks/useResponsive";
import { IBaseUser } from "../../../models/base-user.interface";
import ecgsService from "../../../services/ecgs.service";
import AlertModal from "../../Shared/AlertModal/AlertModal";
import { Button } from "@shltelemedicine/shl-ui-lib";
import DateTime from "../../Shared/DateTime/DateTime";
import NextPrevIcon from "../../Shared/NextPrevIcon/NextPrevIcon";
import styles from "./UserEcgsHeader.module.css";

type UserEcgsHeaderProps = {
  user: IBaseUser | null;
  breadcrumbs: string[] | null;
  minGraphWidth?: number;
  onBack?: MouseEventHandler<HTMLElement> | undefined;
};
const UserEcgsHeader = ({
  user,
  breadcrumbs,
  minGraphWidth,
  onBack,
}: UserEcgsHeaderProps) => {
  const {
    state: { selectedEcg, compareEcg },
    dispatch,
  } = useEcgDetailsContext();
  const isCroOrg = useAuthorizedAction(AuthorizedActionsKeys.ViewAsCroOrg);
  const isCroExternOrg = useAuthorizedAction(
    AuthorizedActionsKeys.ViewAsCroExternOrg
  );

  const { isTablet } = useResponsive();

  const {
    showErrorAlert,
    alertProps,
    alertMessage,
    getAlertMessageAndSupportContent,
  } = useAlert();

  const {
    getToken: getCancelToken,
    isCanceled,
    disposeToken,
  } = useCancelRequests(UserEcgsHeader.name);

  const stopCompareHandler = async () => {
    const { id: cancelTokenId, cancelToken } = getCancelToken();

    try {
      dispatch({
        type: EcgDetailsActionType.SET_LOADING,
        payload: true,
      });

      dispatch({
        type: EcgDetailsActionType.SET_SELECTED_COMPARE_ECG,
        payload: null,
      });

      if (!selectedEcg) {
        showErrorAlert(
          <FormattedMessage
            id="ERROR.LOADING_ECG"
            defaultMessage="An error occurred while loading ECG data.<br></br>Please try again."
            values={{
              br: () => <br></br>,
            }}
          />
        );
        return null;
      }

      // Reloading data since ECG graph now contains compared images
      const { success, data, errorCode } = await ecgsService.getEcgDetails({
        id: selectedEcg.id,
        cancelToken,
      });

      if (isCanceled(cancelTokenId)) {
        return;
      }

      if (!success || !data) {
        const { message, support } = getAlertMessageAndSupportContent(
          "ERROR.LOADING_ECG",
          "An error occurred while loading ECG data.<br></br>Please try again.",
          {
            br: () => <br></br>,
          },
          errorCode
        );
        showErrorAlert(message, support);
        return null;
      }

      const {
        ecgDetails: { graph },
      } = data;

      dispatch({
        type: EcgDetailsActionType.SET_SELECTED_ECG_DETAILS,
        payload: {
          ...selectedEcg,
          graph,
        },
      });
    } finally {
      dispatch({
        type: EcgDetailsActionType.SET_LOADING,
        payload: false,
      });
      disposeToken(cancelTokenId);
    }
  };
  const headerLeftStyle: CSSProperties = minGraphWidth
    ? {
        width: `calc(${minGraphWidth}vw - 0.6%)`,
        maxWidth: `calc(${minGraphWidth}vw - 0.6%)`,
      }
    : {};

  return (
    <header
      className={`${styles.header} p-sm-2 app-shadow d-flex align-items-center`}
    >
      <div
        className="flex-grow-1 d-flex align-items-center justify-content-start"
        style={headerLeftStyle}
      >
        {isTablet && (
          <Button variant="" className="p-0" onClick={onBack} disabled={false}>
            <NextPrevIcon disabled={false} />
          </Button>
        )}
        {breadcrumbs && (
          <Breadcrumb className="mb-0">
            {breadcrumbs.map((b, i) => (
              <Breadcrumb.Item
                key={b + i}
                linkAs="span"
                active={i === breadcrumbs.length - 1}
              >
                {b}
              </Breadcrumb.Item>
            ))}
          </Breadcrumb>
        )}
        {user && (
          <h5 className={`${!breadcrumbs ? "py-2" : ""} mb-0 font-weight-bold`}>
            {user?.firstName} {user?.lastName}
          </h5>
        )}
        {!user && (
          <h5 className="mb-0 py-2 font-weight-bold">
            <FormattedMessage
              id="USER_ECGS.EMPTY_TITLE"
              defaultMessage="Select Patient"
            />
          </h5>
        )}
        {compareEcg && (
          <div className="px-2">
            <Button
              className={`${styles.cancelCompareBtn} d-flex flex-row align-items-center py-1 px-2`}
              onClick={stopCompareHandler}
            >
              <span>&#10006;</span>
              <span className="mx-sm"></span>
              <FormattedMessage
                id="ECG_DETAILS.STOP_COMPARE"
                defaultMessage="Stop Compare"
              />
            </Button>
            <div className="mx-1"></div>
          </div>
        )}
      </div>

      {user && (
        <div className="flex-grow-1 d-sm-flex align-items-center justify-content-between px-2">
          <div className="d-flex align-items-end">
            <div className={styles.mutedTxt}>
              {isCroOrg || isCroExternOrg ? (
                <FormattedMessage id="PATIENT.ECG.YOB" defaultMessage="YOB" />
              ) : (
                <FormattedMessage id="PATIENT.ECG.DOB" defaultMessage="DOB" />
              )}
              :
            </div>
            &nbsp;
            <h5 className="mb-0">
              {isCroOrg || isCroExternOrg ? (
                <Moment format={Constants.YEAR_FORMAT}>
                  {user?.dateOfBirth}
                </Moment>
              ) : (
                <DateTime>{user?.dateOfBirth}</DateTime>
              )}
            </h5>
          </div>
          <div className="d-flex align-items-end">
            <span className={styles.mutedTxt}>
              <FormattedMessage id="PATIENT.GENDER" defaultMessage="Gender" />:
            </span>
            &nbsp;
            <h5 className="mb-0">{user?.gender}</h5>
          </div>
        </div>
      )}
      <AlertModal {...alertProps}>{alertMessage}</AlertModal>
    </header>
  );
};

export default UserEcgsHeader;
